import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import MobileMediaViewer from './MobileMediaViewer';
import MobileMediaViewerPdf from './MobileMediaViewerPdf';
import MobileMediaViewerOffice from './MobileMediaViewerOffice';
import { DiscussionAutoplayVideo } from '../../../newsintelligence/support/openDiscussions/lib/discussionMedia';
import LockFolderStreamingVideo from '../../../storageBox/components/LockFolderStreamingVideo';
import { fetchAndDecryptLockFile, revokeObjectUrl } from '../../../storageBox/utils/lockFileAccess';
import './MobileMediaViewer.css';

function LockFolderDecryptingImage({
  lockSource,
  getMasterKeyBytes,
  alt,
  onDismiss,
  onDismissProgress,
  onSwipeHorizontal,
}) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    const key = getMasterKeyBytes?.();
    if (!lockSource?.item?.itemId || !lockSource.lockSessionToken || !key) {
      setSrc('');
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    fetchAndDecryptLockFile(lockSource.item, lockSource.lockSessionToken, key)
      .then(({ blob }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc('');
      });
    return () => {
      cancelled = true;
      revokeObjectUrl(objectUrl);
      setSrc('');
    };
  }, [lockSource?.item?.itemId, lockSource?.lockSessionToken, getMasterKeyBytes]);

  if (!src) {
    return <p className="mobile-media-viewer-overlay__loading">Loading…</p>;
  }
  return (
    <MobileMediaViewer
      src={src}
      alt={alt}
      onDismiss={onDismiss}
      onDismissProgress={onDismissProgress}
      onSwipeHorizontal={onSwipeHorizontal}
    />
  );
}

const getSafeIndex = (index, length) => {
  if (!length) return 0;
  return ((index % length) + length) % length;
};

/**
 * Fullscreen iPhone-style media overlay: pinch zoom, drag down to dismiss, swipe between images.
 */
export default function MobileMediaViewerOverlay({
  isOpen,
  onClose,
  items = [],
  index = 0,
  onIndexChange,
  getLockMasterKeyBytes,
  lockVaultReady = true,
  onLockVideoError,
}) {
  const [dismissProgress, setDismissProgress] = useState(0);
  const safeIndex = getSafeIndex(index, items.length);
  const activeItem = items[safeIndex] || null;

  const imageOnlyItems = useMemo(
    () =>
      items.filter(
        (item) => item?.type !== 'video' && item?.type !== 'pdf' && item?.type !== 'office' && item?.url
      ),
    [items]
  );

  const activeImageIndex = useMemo(() => {
    if (!activeItem || activeItem.type === 'video' || activeItem.type === 'pdf' || activeItem.type === 'office') {
      return -1;
    }
    return imageOnlyItems.findIndex((item) => item.url === activeItem.url);
  }, [activeItem, imageOnlyItems]);

  useEffect(() => {
    if (!isOpen) {
      setDismissProgress(0);
      return undefined;
    }

    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);

    return () => {
      body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen, onClose]);

  const goToRelative = useCallback(
    (delta) => {
      if (!items.length || !onIndexChange) return;
      onIndexChange(getSafeIndex(safeIndex + delta, items.length));
      setDismissProgress(0);
    },
    [items.length, onIndexChange, safeIndex]
  );

  const handleSwipeHorizontal = useCallback(
    (direction) => {
      if (items.length <= 1) return;
      goToRelative(direction === 'left' ? 1 : -1);
    },
    [items.length, goToRelative]
  );

  const hasRenderable =
    Boolean(activeItem?.url) ||
    Boolean(activeItem?.lockSource?.item && getLockMasterKeyBytes?.());
  if (!isOpen || !activeItem || !hasRenderable) return null;

  const overlayOpacity = dismissProgress > 0 ? Math.max(0.2, 1 - dismissProgress / 280) : 1;

  return createPortal(
    <div
      className="mobile-media-viewer-overlay"
      style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})` }}
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
    >
      <button
        type="button"
        className="mobile-media-viewer-overlay__close"
        onClick={onClose}
        aria-label="Close"
      >
        <FiX aria-hidden />
      </button>

      {items.length > 1 ? (
        <div className="mobile-media-viewer-overlay__counter" aria-live="polite">
          {safeIndex + 1} / {items.length}
        </div>
      ) : null}

      <div className="mobile-media-viewer-overlay__stage">
        {activeItem.type === 'video' ? (
          <div className="mobile-media-viewer-overlay__video-wrap">
            {activeItem.lockSource?.item && getLockMasterKeyBytes ? (
              <LockFolderStreamingVideo
                className="mobile-media-viewer-overlay__video"
                item={activeItem.lockSource.item}
                lockSessionToken={activeItem.lockSource.lockSessionToken}
                masterKeyBytes={getLockMasterKeyBytes()}
                active={isOpen}
                posterUrl={activeItem.posterUrl}
                onError={onLockVideoError}
              />
            ) : (
              <DiscussionAutoplayVideo
                className="mobile-media-viewer-overlay__video"
                src={activeItem.url}
                poster={activeItem.posterUrl}
                active={isOpen}
              />
            )}
          </div>
        ) : activeItem.type === 'pdf' ? (
          <MobileMediaViewerPdf
            key={activeItem.itemId || activeItem.url || 'pdf'}
            src={activeItem.url}
            title={activeItem.alt || 'PDF'}
            fetchCredentials={activeItem.fetchCredentials ?? 'include'}
            pendingMessage={
              !activeItem.url && activeItem.lockSource
                ? lockVaultReady
                  ? 'Loading PDF…'
                  : 'Unlock Lock folder to preview this file.'
                : !activeItem.url
                  ? 'File not available'
                  : ''
            }
          />
        ) : activeItem.type === 'office' ? (
          <MobileMediaViewerOffice
            key={activeItem.url}
            src={activeItem.url}
            fileName={activeItem.fileName || activeItem.alt || ''}
            officeKind={activeItem.officeKind}
            fetchCredentials={activeItem.fetchCredentials ?? 'include'}
          />
        ) : activeItem.lockSource?.item && !activeItem.url && getLockMasterKeyBytes ? (
          <LockFolderDecryptingImage
            key={activeItem.itemId}
            lockSource={activeItem.lockSource}
            getMasterKeyBytes={getLockMasterKeyBytes}
            alt={activeItem.alt || ''}
            onDismiss={onClose}
            onDismissProgress={setDismissProgress}
            onSwipeHorizontal={items.length > 1 ? handleSwipeHorizontal : undefined}
          />
        ) : (
          <MobileMediaViewer
            key={activeItem.url}
            src={activeItem.url}
            alt={activeItem.alt || ''}
            onDismiss={onClose}
            onDismissProgress={setDismissProgress}
            onSwipeHorizontal={items.length > 1 ? handleSwipeHorizontal : undefined}
          />
        )}
      </div>

      {imageOnlyItems.length > 1 && activeImageIndex >= 0 ? (
        <div className="mobile-media-viewer-overlay__dots" aria-hidden>
          {imageOnlyItems.map((item, idx) => (
            <span
              key={item.url || idx}
              className={`mobile-media-viewer-overlay__dot${
                idx === activeImageIndex ? ' mobile-media-viewer-overlay__dot--active' : ''
              }`}
            />
          ))}
        </div>
      ) : null}
    </div>,
    document.body
  );
}
